
import React, { useState, useEffect } from "react";
import {
  Container,
  Col,
  Row,
  Card,
  Form,
  Button,
  Modal,
  Alert,
} from "@themesberg/react-bootstrap";
import Profile1 from "../assets/img/team/profile-picture-1.jpg";
import ProfileCover from "../assets/img/profile-cover.jpg";
import { getImageUrl } from "../utils/imageUrl";
import AxiosInstance from "../AxiosInstance";

export const GeneralInfoForm = () => {
  const [userData, setUserData] = useState(null);
  const [formData, setFormData] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Récupérer l'utilisateur via AxiosInstance (cookie httpOnly)
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await AxiosInstance.get("/api/users/profile");
        const user = response.data?.user || response.data;

        const initialData = {
          name: user.name || "",
          email: user.email || "",
          phone: user.phone || "",
          location: user.location || "",
          role: user.role || "",
          avatar: user.avatar || null,
        };

        setUserData(initialData);
        setFormData(initialData);
        setAvatarPreview(
          initialData.avatar ? getImageUrl(initialData.avatar) : Profile1
        );
      } catch (err) {
        console.error("Erreur API:", err);
        setError("Vérifiez votre connexion internet !");
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, []);

  // Masquer la modale après 3 secondes
  useEffect(() => {
    if (showModal) {
      const timer = setTimeout(() => {
        setShowModal(false);
        // ✅ Recharge silencieuse
        window.location.reload();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [showModal]);


  if (loading) return <p>Chargement des informations...</p>;
  if (error) return <Alert variant="danger">{error}</Alert>;
  if (!userData) return null;

  // Handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, avatar: file }));
      setAvatarPreview(URL.createObjectURL(file));
    }
  };

  const handleCancel = () => {
    setFormData(userData);
    setAvatarPreview(userData.avatar ? getImageUrl(userData.avatar) : Profile1);
    setEditing(false);
    setError(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      let body;
      let headers = {};

      if (formData.avatar instanceof File) {
        body = new FormData();
        body.append("name", formData.name);
        body.append("phone", formData.phone);
        body.append("location", formData.location);
        body.append("avatar", formData.avatar);
      } else {
        headers["Content-Type"] = "application/json";
        body = {
          name: formData.name,
          phone: formData.phone,
          location: formData.location,
          avatar: formData.avatar,
        };
      }

      await AxiosInstance.put("/api/users/profile", body, { headers });

      // Re-fetch updated user
      const refreshed = await AxiosInstance.get("/api/users/profile");
      const user = refreshed.data?.user || refreshed.data;

      const updated = {
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        location: user.location || "",
        role: user.role || "",
        avatar: user.avatar || null,
      };

      setUserData(updated);
      setFormData(updated);
      setAvatarPreview(updated.avatar ? getImageUrl(updated.avatar) : Profile1);
      setEditing(false);
      setShowModal(true);
    } catch (err) {
      console.error("Erreur API:", err);
      setError("Erreur lors de la mise à jour du profil.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        dialogClassName="p-0"
      >
        <div
          style={{
            maxWidth: "300px",
            width: "70%",
            height: "120px",
            margin: "auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
          }}
        >
          <Modal.Body style={{ padding: 0, fontSize: "1.1rem" }}>
            ✅ Profil mis à jour avec succès !
          </Modal.Body>
        </div>
      </Modal>




      {/* ✅ Formulaire utilisateur */}
      <Row style={{ paddingLeft: "15px", paddingRight: "15px" }}>
        <Col lg={8} md={12}>
          <Card border="light" className="bg-white shadow-sm mb-4">
            <Card.Body>
              <h5 className="mb-4">Information Générale</h5>
              {error && <Alert variant="danger">{error}</Alert>}
              <Form>
                <Row>
                  <Col sm={12} className="mb-3">
                    <Form.Group>
                      <Form.Label>Nom complet</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        readOnly={!editing}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>Email</Form.Label>
                      <Form.Control type="email" value={formData.email} readOnly />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>Téléphone</Form.Label>
                      <Form.Control
                        type="text"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        readOnly={!editing}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <h5 className="my-4">Adresse</h5>
                <Row>
                  <Col sm={12} className="mb-3">
                    <Form.Group>
                      <Form.Label>Adresse (ville/localisation)</Form.Label>
                      <Form.Control
                        type="text"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        readOnly={!editing}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {editing && (
                  <Form.Group className="mb-3">
                    <Form.Label>Changer de Photo de profile</Form.Label>
                    <Form.Control type="file" accept="image/*" onChange={handleAvatarChange} />
                  </Form.Group>
                )}

                <div className="mt-3">
                  {!editing ? (
                    <Button variant="primary" onClick={() => setEditing(true)}>
                      Modifier
                    </Button>
                  ) : (
                    <>
                      <Button variant="success" onClick={handleSave} disabled={saving}>
                        {saving ? "Enregistrement..." : "Enregistrer"}
                      </Button>{" "}
                      <Button variant="secondary" onClick={handleCancel} disabled={saving}>
                        Annuler
                      </Button>
                    </>
                  )}
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4} md={12}>
          <Card border="light" className="text-center p-0 mb-4">
            <div
              style={{ backgroundImage: `url(${ProfileCover})` }}
              className="profile-cover rounded-top"
            />
            <Card.Body className="pb-5">
              <Card.Img
                src={avatarPreview}
                alt="Avatar"
                className="user-avatar large-avatar rounded-circle mx-auto mt-n7 mb-4"
              />
              <Card.Title>{formData.name}</Card.Title>
              <Card.Subtitle className="fw-normal">{formData.role}</Card.Subtitle>
              <Card.Text className="text-gray mb-4">{formData.location}</Card.Text>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};
